import { Resolvers } from 'apollo-client'

import {
  SignInMutation,
  SignInMutationVariables,
  SignInWithSsoMutation,
  SignInWithSsoMutationVariables,
  RefreshCredentialsMutationVariables,
  RefreshCredentialsMutation,
  RevokeOAuthMutation,
  RevokeOAuthMutationVariables,
} from 'gqlTypes'
import {
  createExpireDate,
  getSignInEndpoint,
  getSignInWithSsoEndpoint,
  getRevokeOAuthEndpoint,
} from 'graphql/utils'
import { ErrorHandler } from 'modules/tracing'

type ResponseType =
  | SignInMutation['signIn']['__typename']
  | SignInWithSsoMutation['signInWithSso']['__typename']
  | RefreshCredentialsMutation['refreshCredentials']['__typename']

const SIGN_IN_WITH_SSO_ENDPOINT = getSignInWithSsoEndpoint()

const formatCredentials = (responseBody: any) => {
  const { access_token, refresh_token, expires_in, token_type } = responseBody

  if (token_type === 'basic') {
    ErrorHandler.shouldNeverHappen('Unexpected basic token type')
  }

  return {
    __typename: 'OAuthCredentials' as const,
    authToken: access_token,
    refreshToken: refresh_token,
    expirationDateTime: createExpireDate(expires_in),
  }
}

const formatResponseToGraphQL = async (
  responseType: ResponseType,
  response: Response
) => {
  const responseJson = await response.json()

  if (response.status === 200) {
    const credentials = formatCredentials(responseJson)

    // In case of a successful response but the credentials are not returned
    if (
      !credentials.authToken ||
      !credentials.refreshToken ||
      (responseType === 'SignInWithSsoResponse' && !responseJson.organization)
    ) {
      throw new Error('Something went wrong')
    }

    return {
      credentials,
      workspaceId: responseJson.organization,
    }
  }

  throw new Error(responseJson.message || 'Something went wrong')
}

type SignIn = (
  root: any,
  params: SignInMutationVariables
) => Promise<SignInMutation['signIn']>

const signIn: SignIn = async (_, { email, password }) => {
  const response = await fetch(getSignInEndpoint(), {
    method: 'post',
    body: JSON.stringify({ email, password, grant_type: 'password' }),
    headers: { 'Content-Type': 'application/json' },
  })

  const graphQLResponse = await formatResponseToGraphQL(
    'SignInResponse',
    response
  )

  return {
    __typename: 'SignInResponse',
    ...graphQLResponse,
  }
}

type RefreshCredentials = (
  root: any,
  params: RefreshCredentialsMutationVariables
) => Promise<RefreshCredentialsMutation['refreshCredentials']>

const refreshCredentials: RefreshCredentials = async (
  _,
  { authToken, refreshToken }
) => {
  const response = await fetch(getSignInEndpoint(), {
    method: 'post',
    body: JSON.stringify({
      access_token: authToken,
      refresh_token: refreshToken,
      grant_type: 'refresh_token',
    }),
    headers: { 'Content-Type': 'application/json' },
  })

  const graphQLResponse = await formatResponseToGraphQL(
    'RefreshCredentialsResponse',
    response
  )

  return {
    __typename: 'RefreshCredentialsResponse',
    ...graphQLResponse,
  }
}

type SignInWithSso = (
  root: any,
  params: SignInWithSsoMutationVariables
) => Promise<SignInWithSsoMutation['signInWithSso']>

const signInWithSso: SignInWithSso = async (_, { exchangeToken }) => {
  const response = await fetch(SIGN_IN_WITH_SSO_ENDPOINT, {
    method: 'post',
    body: JSON.stringify({ token: exchangeToken }),
    headers: { 'Content-Type': 'application/json' },
  })

  const graphQLResponse = await formatResponseToGraphQL(
    'SignInWithSsoResponse',
    response
  )

  return {
    __typename: 'SignInWithSsoResponse',
    ...graphQLResponse,
  }
}

type RevokeOAuth = (
  root: any,
  params: RevokeOAuthMutationVariables
) => Promise<RevokeOAuthMutation['revokeOAuth']>

const revokeOAuth: RevokeOAuth = async (_, { token }) => {
  const response = await fetch(getRevokeOAuthEndpoint(), {
    method: 'post',
    body: JSON.stringify({ token, token_type_hint: 'access_token' }),
    headers: { 'Content-Type': 'application/json' },
  })

  if (response.status === 200) {
    return {
      __typename: 'RevokeOAuthResponse',
      successful: true,
    }
  }

  throw new Error('Something went wrong')
}

export const Mutation: Resolvers[any] = {
  signIn,
  refreshCredentials,
  signInWithSso,
  revokeOAuth,
}
